// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  BarController,
  BarElement,
  PointElement,
  LineController,
  LineElement,
  LinearScale,
  TimeScale,
  ScatterDataPoint,
  Tooltip,
  TooltipItem,
} from "chart.js";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import zoomPlugin from "chartjs-plugin-zoom";
import { Chart } from "react-chartjs-2";
import { Duration, DateTime } from "luxon";

import { isSet, roundToDigits } from "shared/utils/utils";
import {
  hexToRgba,
  doKiloConversion,
  getEnergyUnit,
  getSiteSelection,
  getGraphBoundaryDatesFromPeriod,
} from "guiutils";
import { ActionType, IAction, raiseAction, IActionTypeZoom } from "actions";
import { TrendMonitor as ITrendMonitor } from "shared/types/cloudtypes";
import TrendMonitorModel from "model/trendmonitor";
import { IState } from "states/states";
import { ITariffCircuit } from "shared/types/parameter";
import { ITrendData } from "states/trendingstates";
import { TimePeriod, DataState } from "types";

import IconZoomFit from "icons/iconZoomFit";

import cssContent from "constants.module.scss";

// register used chartjs elements
ChartJS.register(
  LineController,
  LineElement,
  BarController,
  BarElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  zoomPlugin,
);

const COLOR_SELECT = "#66C0CC"; // "#70adbc"

const DATASET_ALARM = 0;
const DATASET_LIMIT = 1;
const DATASET_VALUE = 2;
const DATASET_OVERSHOOT = 3;

interface ITrendMonitorGraphExtProps {}

type ITrendMonitorGraphProps = ITrendMonitorGraphExtProps & {
  trendMonitor: ITrendMonitor | undefined;
  dataState: DataState;
  // graph boundary, computed using the trend monitor's own configured period
  // (not the page-level xZoom) as zoom
  graphStart: number | undefined;
  graphEnd: number | undefined;
  graphZoom: TimePeriod;
  trendData: ITrendData;
  tariffCircuit: ITariffCircuit | undefined;
  xZoom: TimePeriod;
  minX?: number;
  maxX?: number;
};

const mapStateToProps = (state: IState, ownProps: ITrendMonitorGraphExtProps) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  const trendmonitors = site?.cloudSettings?.trendMonitor ?? [];
  const idxs = TrendMonitorModel.getActiveSortedTrendMonitorIdxs(trendmonitors);

  const selectedTrendMonitor = getSiteSelection(state)?.trendMonitor ?? idxs[0];
  const trendMonitor =
    isSet(selectedTrendMonitor) ?
      site?.cloudSettings?.trendMonitor?.[selectedTrendMonitor]
    : undefined;
  const tariffCircuit = state.para.parameter?.tariffline.find(
    (curItem, curIndex) => (curItem.id ?? curIndex) === trendMonitor?.circuit,
  );
  const graphZoom =
    (trendMonitor?.period as unknown as TimePeriod | undefined) ?? state.gui.trendPeriod.timePeriod;
  const { timePeriod, graphStart, graphEnd } = getGraphBoundaryDatesFromPeriod(
    state.gui.trendPeriod,
    state.site.tzOffset,
    graphZoom,
  );
  const props: ITrendMonitorGraphProps = {
    trendMonitor,
    dataState: state.scheduler.trendLoadingState,
    graphStart,
    graphEnd,
    graphZoom,
    trendData: state.trending.trendData,
    tariffCircuit,
    xZoom: timePeriod,
    minX: state.gui.trendPeriod.zoomMin,
    maxX: state.gui.trendPeriod.zoomMax,
  };
  return props;
};

interface ITrendMonitorGraphDispatch {
  onZooming: (minX?: number, maxX?: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendMonitorGraphDispatch = {
    onZooming: (minX?: number, maxX?: number) => {
      const actionPayload: IActionTypeZoom = {
        identifier: "trendPeriod",
        minX,
        maxX,
      };
      dispatch(raiseAction(ActionType.ACT_ZOOM_GRAPH, actionPayload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  resetZoom: {
    id: "reset zoom",
  },
  graphFormatAxisDay: {
    id: "MMM d",
  },
  graphFormatAxisHour: {
    id: "h a",
  },
  graphFormatAxisMinute: {
    id: "h:mm a",
  },
  labelData: {
    id: "Current data",
  },
  labelLimit: {
    id: "Limit value",
  },
  labelAlarm: {
    id: "Alarm limit",
  },
  labelOvershoot: {
    id: "Overshoot",
  },
});

// State is never set so we use the 'undefined' type.
class TrendMonitorGraphComponent extends React.Component<
  ITrendMonitorGraphProps & ITrendMonitorGraphDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  private startTime?: number;
  private endTime?: number;
  private data: number[][] = []; // array of [timestamp, y] values
  private doKiloConvertion = false;
  private refData: number[][] = []; // array of reference values
  private alarmLimitData: number[][] = []; // array of alarm limit values

  public render() {
    // make some general calculations
    this.preProcessData();

    // check for existing data
    if (this.props.dataState !== DataState.SAVE_REQ && !this.data.length) {
      return (
        <div id="trendmonitorgraph" className="graph nodata">
          {this.props.dataState === DataState.SAVE_FAIL ?
            <FormattedMessage id="Failed to load data." />
          : this.props.minX || this.props.maxX ?
            <>
              {this.props.tariffCircuit?.name}:{" "}
              <FormattedMessage id="No data available. Try reset zooming." />
              <div
                className="graph-zoom"
                onClick={this.resetZoom.bind(this)}
                title={this.context.formatMessage(messages.resetZoom)}
              >
                <IconZoomFit />
              </div>
            </>
          : <>
              {this.props.tariffCircuit?.name}: <FormattedMessage id="No data available." />
            </>
          }
        </div>
      );
    }

    return (
      <div id="trendmonitorgraph" className="graph">
        {isSet(this.props.minX) || isSet(this.props.maxX) ?
          <div
            className="graph-zoom"
            onClick={this.resetZoom.bind(this)}
            title={this.context.formatMessage(messages.resetZoom)}
          >
            <IconZoomFit />
          </div>
        : <></>}
        {this.props.dataState === DataState.SAVE_REQ && <div id="loading-spinner" />}
        <Chart type="bar" data={this.createGraphData()} options={this.createGraphOptions()} />
      </div>
    );
  }

  private getGraphDateSeries(interval: Duration) {
    const dateSeries = [];
    // if we zoom to all data, we provide no timestamps here
    if (this.props.graphZoom !== TimePeriod.ALL) {
      let curDate = DateTime.fromSeconds(this.props.graphStart!);
      const endDate = DateTime.fromSeconds(this.props.graphEnd!);
      while (curDate < endDate) {
        dateSeries.push(curDate);
        curDate = curDate.plus(interval);
      }
    }
    return {
      startDate: this.props.graphStart,
      endDate: this.props.graphEnd,
      dateSeries,
    };
  }

  private preProcessData() {
    // some general data pre-processing
    const interval = Duration.fromObject(
      this.props.xZoom === TimePeriod.YEAR ? { months: 1 }
      : this.props.xZoom === TimePeriod.MONTH ? { days: 1 }
      : this.props.xZoom === TimePeriod.WEEK ? { hours: 1 }
      : /* TimePeriod.DAY */ { hours: 1 },
    );

    const graphDates = this.getGraphDateSeries(interval);
    this.startTime = this.props.minX ? this.props.minX / 1000 : graphDates.startDate;
    this.endTime = this.props.maxX ? this.props.maxX / 1000 : graphDates.endDate;
    // prepare data over the whole graph!! (otherwise the lines won't get drawn)
    const startTimeData = graphDates.startDate!;
    const endTimeData = graphDates.endDate!;

    this.refData = [];
    this.alarmLimitData = [];
    this.data = [];
    let sumValue = 0;
    // loop through timestamps
    graphDates.dateSeries.forEach((curTimestamp, curIndex) => {
      // get all trend values between this timestamp and the next one
      const nextTimestamp = graphDates.dateSeries[curIndex + 1];
      const value = this.props.trendData
        .filter(
          (row) =>
            row.tlId === this.props.trendMonitor?.circuit &&
            row.timestamp >= curTimestamp.toSeconds() &&
            (nextTimestamp === undefined || row.timestamp < nextTimestamp.toSeconds()),
        )
        .reduce((prev: undefined | number, row) => (prev ?? 0) + (row.energy ?? 0), undefined); // sum up the energies
      // accumulate
      sumValue += value ?? 0;
      // in visible range?
      if (curTimestamp.toSeconds() >= startTimeData && curTimestamp.toSeconds() < endTimeData) {
        const refValue =
          ((this.props.trendMonitor?.targetValue ?? 0) * (curIndex + 1)) /
          graphDates.dateSeries.length;
        this.refData.push([curTimestamp.toSeconds(), refValue]);
        // also refer to lambdacron/trendmonitoralarm.ts
        const alarmValue =
          (this.props.trendMonitor?.targetValue ?? 0) *
          ((this.props.trendMonitor?.tolerance ?? 0) +
            (((this.props.trendMonitor?.slope ?? 1) * (curIndex + 1)) /
              graphDates.dateSeries.length +
              (1 - (this.props.trendMonitor?.slope ?? 1))));
        this.alarmLimitData.push([curTimestamp.toSeconds(), alarmValue]);
        if (isSet(value)) {
          this.data.push([curTimestamp.toSeconds(), sumValue]);
        }
      }
    });

    // derive maximum
    sumValue = Math.max(sumValue, this.props.trendMonitor?.targetValue ?? 0);
    // check whether we need to multiply everything by 10000
    this.doKiloConvertion = doKiloConversion(sumValue);
    if (this.doKiloConvertion) {
      this.data.forEach((curData) => {
        curData[1] *= 1000;
      });
      this.refData.forEach((curData) => {
        curData[1] *= 1000;
      });
      this.alarmLimitData.forEach((curData) => {
        curData[1] *= 1000;
      });
    }
  }

  private getDataUnit() {
    return getEnergyUnit(this.props.tariffCircuit?.unit, this.doKiloConvertion);
  }

  private createGraphOptions() {
    const options: ChartOptions<"bar" | "line"> = {
      scales: {
        x: {
          type: "time",
          time: {
            //unit: this.useBarChart ? "hour" : "day",
            minUnit:
              this.props.xZoom === TimePeriod.YEAR ? "month"
              : this.props.xZoom >= TimePeriod.WEEK ? "day"
              : this.props.xZoom === TimePeriod.DAY ? "hour"
              : "minute",
            displayFormats: {
              minute: this.context.formatMessage(messages.graphFormatAxisMinute),
              hour: this.context.formatMessage(messages.graphFormatAxisHour),
              day: this.context.formatMessage(messages.graphFormatAxisDay),
            },
          },
          stacked: true, // only relevant for bar chart
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: this.getDataUnit(),
          },
          stacked: true, // only relevant for bar chart
        },
      },
      datasets: {
        line: {
          pointRadius: 1, // default 3
          tension: 0.4, // line smoothness, default 0
        },
        bar: {
          barThickness: "flex",
        },
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      plugins: {
        legend: {
          display: false,
        },
        title: {
          text: this.props.tariffCircuit?.name,
        },
        tooltip: {
          enabled: true,
          mode: "x", // show all data items on same x value
          position: "average", // show tooltip on average value, not at mouse cursor
          callbacks: {
            title: (tooltipItems: TooltipItem<"bar" | "line">[]) => {
              const curItem = tooltipItems[0];
              const date = new Date(curItem.parsed.x ?? 0);
              const localeOptions: Intl.DateTimeFormatOptions = {
                year: "numeric",
                month: "short",
                day: this.props.xZoom !== TimePeriod.YEAR ? "numeric" : undefined,
                hour: this.props.xZoom !== TimePeriod.MONTH ? "numeric" : undefined,
                minute: this.props.xZoom !== TimePeriod.MONTH ? "numeric" : undefined,
              };
              let title = date.toLocaleString(this.context.locale, localeOptions);
              // on hourly view, we give the additional hint that the current hour is included
              if (this.props.xZoom === TimePeriod.DAY) {
                const endHour = `0${date.getHours() + 1}`.slice(-2);
                title += `-${endHour}:00`;
              }
              return title;
            },
            label: (tooltipItem: TooltipItem<"bar" | "line">) => {
              let value = tooltipItem.parsed.y;
              // we need to add the overflow bar
              // we need to sum the data and increased data sets to get the total value
              if (tooltipItem.datasetIndex === DATASET_VALUE) {
                const datasets = tooltipItem.chart.data.datasets;
                const curValue = tooltipItem.raw as ScatterDataPoint;
                const increasedVal = (
                  datasets[DATASET_OVERSHOOT]?.data as undefined | ScatterDataPoint[]
                )?.find((curItem) => curItem.x === curValue.x)?.y;
                if (isSet(increasedVal) && value !== null) {
                  value += increasedVal;
                }
              }
              // hide overshoot legend entry if there is no overshoot
              if (tooltipItem.datasetIndex === DATASET_OVERSHOOT && value === 0) {
                // we need to return undefined but this violates type definition...
                return undefined as any as string;
              }
              return ` ${tooltipItem.dataset.label}: ${roundToDigits(
                value,
                tooltipItem.datasetIndex === DATASET_OVERSHOOT ? 2 : 3,
              )} ${this.getDataUnit()}`;
            },
          },
        },
        zoom: {
          pan: {
            enabled: false,
          },
          zoom: {
            mode: "x",
            wheel: {
              enabled: true,
            },
            drag: {
              enabled: true,
              borderColor: hexToRgba(COLOR_SELECT, 0.7),
              backgroundColor: hexToRgba(COLOR_SELECT, 0.5),
            },
            onZoom: (context) => this.zoomCallback(context.chart),
          },
        },
        datalabels: {
          display: false,
        },
      },
    };

    return options;
  }

  private createGraphData() {
    const points: ScatterDataPoint[] = [];
    const increasedPoints: ScatterDataPoint[] = [];
    const refPoints: ScatterDataPoint[] = [];
    const alarmPoints: ScatterDataPoint[] = [];

    // add all data points sequentially
    this.refData.forEach((curItem, curIndex) => {
      const curTimestamp = curItem[0] * 1000;
      const curData = this.data.find((curDataItem) => curDataItem[0] === curItem[0])?.[1];
      const refValue = curItem[1];
      const alarmValue = this.alarmLimitData[curIndex][1];

      // do not show data during loading animation
      if (isSet(curData)) {
        points.push({
          x: curTimestamp,
          y: this.props.dataState === DataState.SAVE_REQ ? 0 : Math.min(curData, refValue),
        });
        increasedPoints.push({
          x: curTimestamp,
          y: this.props.dataState === DataState.SAVE_REQ ? 0 : Math.max(curData - refValue, 0),
        });
      }
      refPoints.push({
        x: curTimestamp,
        y: refValue,
      });
      alarmPoints.push({
        x: curTimestamp,
        y: alarmValue,
      });
    });

    const data: ChartData<"bar" | "line", ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    data.datasets[DATASET_ALARM] = {
      label: this.context.formatMessage(messages.labelAlarm),
      data: alarmPoints,
      type: "line",
      borderDash: [5, 5],
      borderColor: cssContent.GRAPH_COLOR_TREND,
      backgroundColor: cssContent.GRAPH_COLOR_TREND,
      stack: `${DATASET_ALARM}`,
      order: 0,
    };
    data.datasets[DATASET_VALUE] = {
      label: this.context.formatMessage(messages.labelData),
      data: points,
      type: "bar",
      borderColor: cssContent.GRAPH_COLOR,
      backgroundColor: cssContent.GRAPH_COLOR,
      stack: "energy-stack",
    };
    data.datasets[DATASET_LIMIT] = {
      label: this.context.formatMessage(messages.labelLimit),
      data: refPoints,
      type: "line",
      borderColor: cssContent.GRAPH_COLOR_TARGET,
      backgroundColor: cssContent.GRAPH_COLOR_TARGET,
      stack: `${DATASET_LIMIT}`,
    };
    data.datasets[DATASET_OVERSHOOT] = {
      label: this.context.formatMessage(messages.labelOvershoot),
      data: increasedPoints,
      type: "bar",
      borderColor: cssContent.GRAPH_COLOR_OVERSHOOT,
      backgroundColor: cssContent.GRAPH_COLOR_OVERSHOOT,
      stack: "energy-stack",
    };
    return data;
  }

  private zoomCallback(chart: ChartJS) {
    const axis = chart.scales.x;
    this.props.onZooming(axis.min, axis.max);
  }

  private resetZoom() {
    this.props.onZooming();
  }
}

const TrendMonitorGraph = connect(mapStateToProps, mapDispatchToProps)(TrendMonitorGraphComponent);

export default TrendMonitorGraph;
